/**
 * T-046: typed client for /admin/pocket-settings/*.
 */

import { authenticatedFetch } from '@/lib/api';
import type {
  PocketAasaInfo,
  PocketCacheFlushScope,
  PocketDashboardCacheFlushResult,
  PocketDashboardCacheStatus,
  OutboxDrainSummary,
  OutboxHealthStatus,
  FleetQrStatus,
  PocketCheckinPhotoConfig,
  PocketDamagedReturns,
  PocketFleetStatus,
  PocketHealth,
  PocketIdempotencyStatus,
  PocketQrDefaults,
  PocketQrRollup,
  PocketSalesIntegration,
  PocketTelemetry,
  RateLimitEntry,
  RateLimitsList,
  RateLimitUpdateBody,
  StalledRow,
  StalledRowResponse,
  StalledRowSource,
  StalledRowState,
  StalledRowsResponse,
} from '@/types/pocketSettings';

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    const body = ct.includes('application/json') ? await res.json() : null;
    const err = new Error(
      body?.message || body?.code || `Request failed with status ${res.status}`,
    );
    throw err;
  }
  return (await res.json()) as T;
}

export async function getQrRollup(): Promise<PocketQrRollup> {
  const res = await authenticatedFetch('/api/v1/admin/pocket-settings/qr-rollup');
  return jsonOrThrow<PocketQrRollup>(res);
}

export async function getAasaInfo(): Promise<PocketAasaInfo> {
  const res = await authenticatedFetch('/api/v1/admin/pocket-settings/aasa');
  return jsonOrThrow<PocketAasaInfo>(res);
}

export async function getDashboardCacheStatus(): Promise<PocketDashboardCacheStatus> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/dashboard-cache',
  );
  return jsonOrThrow<PocketDashboardCacheStatus>(res);
}

export async function getRateLimits(): Promise<RateLimitsList> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/rate-limits',
  );
  return jsonOrThrow<RateLimitsList>(res);
}

export async function updateRateLimit(
  endpointType: string,
  body: RateLimitUpdateBody,
): Promise<{ entry: RateLimitEntry }> {
  const res = await authenticatedFetch(
    `/api/v1/admin/pocket-settings/rate-limits/${encodeURIComponent(endpointType)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow<{ entry: RateLimitEntry }>(res);
}

export async function flushDashboardCache(
  scope: PocketCacheFlushScope,
): Promise<PocketDashboardCacheFlushResult> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/dashboard-cache/flush',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scope }),
    },
  );
  return jsonOrThrow<PocketDashboardCacheFlushResult>(res);
}

export async function getFleetQrStatus(missingOnly = false): Promise<FleetQrStatus> {
  const qs = missingOnly ? '?missing_only=true' : '';
  const res = await authenticatedFetch(
    `/api/v1/admin/pocket-settings/fleet-qr-status${qs}`,
  );
  return jsonOrThrow<FleetQrStatus>(res);
}

export async function getPocketHealth(): Promise<PocketHealth> {
  const res = await authenticatedFetch('/api/v1/admin/pocket-settings/health');
  return jsonOrThrow<PocketHealth>(res);
}

export async function getOutboxHealth(): Promise<OutboxHealthStatus> {
  const res = await authenticatedFetch('/api/v1/admin/pocket-settings/outbox');
  return jsonOrThrow<OutboxHealthStatus>(res);
}

export async function forceDrainOutbox(): Promise<{ summary: OutboxDrainSummary }> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/outbox/force-drain',
    { method: 'POST' },
  );
  return jsonOrThrow<{ summary: OutboxDrainSummary }>(res);
}

export async function getIdempotencyStatus(): Promise<PocketIdempotencyStatus> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/idempotency',
  );
  return jsonOrThrow<PocketIdempotencyStatus>(res);
}

export async function forceReapIdempotency(): Promise<{ reaped: number }> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/idempotency/reap',
    { method: 'POST' },
  );
  return jsonOrThrow<{ reaped: number }>(res);
}

// ISS-031: per-row drill-down for stalled outbox / cron-failure rows.
export async function listStalledRows(opts: {
  source: StalledRowSource;
  state?: StalledRowState;
  limit?: number;
  offset?: number;
}): Promise<StalledRowsResponse> {
  const params = new URLSearchParams({ source: opts.source });
  if (opts.state) params.set('state', opts.state);
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.offset != null) params.set('offset', String(opts.offset));
  const res = await authenticatedFetch(
    `/api/v1/admin/pocket-settings/outbox/stalled?${params.toString()}`,
  );
  return jsonOrThrow<StalledRowsResponse>(res);
}

export async function retryStalledRow(
  source: 'sales' | 'internal',
  id: string,
): Promise<StalledRowResponse<StalledRow>> {
  const res = await authenticatedFetch(
    `/api/v1/admin/pocket-settings/outbox/${source}/${encodeURIComponent(id)}/retry`,
    { method: 'POST' },
  );
  return jsonOrThrow<StalledRowResponse<StalledRow>>(res);
}

export async function deadLetterStalledRow(
  source: 'sales' | 'internal',
  id: string,
  reason?: string,
): Promise<StalledRowResponse<StalledRow>> {
  const res = await authenticatedFetch(
    `/api/v1/admin/pocket-settings/outbox/${source}/${encodeURIComponent(id)}/dead-letter`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
  return jsonOrThrow<StalledRowResponse<StalledRow>>(res);
}

export async function getFleetStatus(): Promise<PocketFleetStatus> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/fleet-status',
  );
  return jsonOrThrow<PocketFleetStatus>(res);
}

export async function getTelemetry(): Promise<PocketTelemetry> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/telemetry',
  );
  return jsonOrThrow<PocketTelemetry>(res);
}

export async function getSalesIntegration(): Promise<PocketSalesIntegration> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/sales-integration',
  );
  return jsonOrThrow<PocketSalesIntegration>(res);
}

export async function getCheckinPhotoConfig(): Promise<PocketCheckinPhotoConfig> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/checkin-photo-config',
  );
  return jsonOrThrow<PocketCheckinPhotoConfig>(res);
}

export async function updateCheckinPhotoConfig(body: {
  max_bytes: number;
  max_count: number;
}): Promise<{ max_bytes: number; max_count: number }> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/checkin-photo-config',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow<{ max_bytes: number; max_count: number }>(res);
}

export async function getDamagedReturns(): Promise<PocketDamagedReturns> {
  const res = await authenticatedFetch('/api/v1/admin/pocket-settings/damaged-returns');
  return jsonOrThrow<PocketDamagedReturns>(res);
}

export async function updateDamagedReturns(body: { hold: boolean }): Promise<PocketDamagedReturns> {
  const res = await authenticatedFetch('/api/v1/admin/pocket-settings/damaged-returns', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<PocketDamagedReturns>(res);
}

export async function getQrDefaults(): Promise<PocketQrDefaults> {
  const res = await authenticatedFetch('/api/v1/admin/pocket-settings/qr-defaults');
  return jsonOrThrow<PocketQrDefaults>(res);
}

export async function updateQrDefaults(body: {
  default_size_px: number;
}): Promise<{ default_size_px: number }> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/qr-defaults',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow<{ default_size_px: number }>(res);
}

export async function updateDashboardCacheConfig(body: {
  ttl_seconds: number;
  swr_seconds: number;
}): Promise<{ ttl_seconds: number; swr_seconds: number }> {
  const res = await authenticatedFetch(
    '/api/v1/admin/pocket-settings/dashboard-cache/config',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow<{ ttl_seconds: number; swr_seconds: number }>(res);
}
